import type { CharacterSnapshot, CharacterState, IsoPoint } from '@modo-vivo/contracts';
import { distanceIso } from '../iso.js';

export interface PathFollowOptions {
  speed: number;
  onStateChange?: (state: CharacterState) => void;
  onComplete?: () => void;
}

export class CharacterController {
  readonly id: string;
  type: CharacterSnapshot['type'];
  displayName: string;
  position: IsoPoint;
  state: CharacterState = 'idle';
  animation = 'idle';
  destination?: IsoPoint;
  currentAction?: string;

  private path: IsoPoint[] = [];
  private pathIndex = 0;
  private speed = 2.4;
  private onStateChange?: (state: CharacterState) => void;
  private onPathComplete?: () => void;

  constructor(snapshot: CharacterSnapshot) {
    this.id = snapshot.id;
    this.type = snapshot.type;
    this.displayName = snapshot.displayName;
    this.position = { ...snapshot.position };
    this.state = snapshot.state;
    this.animation = snapshot.animation;
  }

  snapshot(): CharacterSnapshot {
    return {
      id: this.id,
      type: this.type,
      displayName: this.displayName,
      position: { ...this.position },
      state: this.state,
      animation: this.animation,
      destination: this.destination ? { ...this.destination } : undefined,
      currentAction: this.currentAction,
    };
  }

  followPath(points: IsoPoint[], options?: PathFollowOptions): void {
    if (points.length === 0) {
      return;
    }
    this.path = points.map((p) => ({ ...p }));
    this.pathIndex = 0;
    this.speed = options?.speed ?? this.speed;
    this.onStateChange = options?.onStateChange;
    this.onPathComplete = options?.onComplete;
    this.setState('walking');
    this.destination = { ...this.path[this.path.length - 1] };
  }

  setState(state: CharacterState, animation?: string): void {
    this.state = state;
    this.animation = animation ?? state;
    this.onStateChange?.(state);
  }

  update(deltaMs: number): void {
    if (this.state !== 'walking' || this.pathIndex >= this.path.length) {
      return;
    }
    const target = this.path[this.pathIndex];
    const step = (this.speed * deltaMs) / 1000;
    const dist = distanceIso(this.position, target);
    if (dist <= step) {
      this.position = { ...target };
      this.pathIndex += 1;
      if (this.pathIndex >= this.path.length) {
        this.setState('idle');
        this.destination = undefined;
        this.onPathComplete?.();
        this.onPathComplete = undefined;
      }
      return;
    }
    const t = step / dist;
    this.position = {
      x: this.position.x + (target.x - this.position.x) * t,
      y: this.position.y + (target.y - this.position.y) * t,
    };
  }
}
